import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { classNames as cn } from '../utils';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList, } from './Command';
import FieldTitle from './FieldTitle';
import Input from './Input';
import { Popover, PopoverAnchor, PopoverContent } from './popover';
export default function Autocomplete({ title, titlePosition, selected, onChange, options, getValue, getSubtitle, getIcon, getOptionId, getGroup, renderOption, onInputChange, filter, loading, disabled, readOnly, placeholder = 'Select...', emptyMessage = 'No results found.', className, modal, size = 'default', }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const _getValue = getValue || ((a) => `${a}`);
    const _getSubtitle = getSubtitle;
    const _getIcon = getIcon;
    const _getOptionId = getOptionId || ((a) => `${a}`);
    const _filter = filter ||
        ((query, options) => options.filter((option) => {
            return _getValue(option).toLowerCase().includes(query.toLowerCase());
        }));
    const filtered = query === '' ? options : _filter(query, options);
    // When closed, reset query and show the selected value
    useEffect(() => {
        if (!open) {
            setQuery('');
            setHighlightedIndex(-1);
        }
    }, [open]);
    // Reset highlighted index when filtered options change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filtered.length]);
    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('[data-autocomplete-item]');
            const item = items[highlightedIndex];
            item === null || item === void 0 ? void 0 : item.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);
    const handleSelect = (option) => {
        onChange(option);
        setOpen(false);
    };
    const handleKeyDown = (e) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => prev < filtered.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => prev > 0 ? prev - 1 : filtered.length - 1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
                    handleSelect(filtered[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                break;
        }
    };
    // Display the selected value when not focused, otherwise show query
    const displayValue = selected ? _getValue(selected) : '';
    const inputValue = open ? query : displayValue;
    // Helper to render option item
    const renderOptionItem = (option, index, showGroupHeader, groupName) => {
        const isSelected = selected && _getOptionId(option) === _getOptionId(selected);
        const isHighlighted = index === highlightedIndex;
        const icon = _getIcon === null || _getIcon === void 0 ? void 0 : _getIcon(option);
        const subtitle = _getSubtitle === null || _getSubtitle === void 0 ? void 0 : _getSubtitle(option);
        return (_jsxs(React.Fragment, { children: [showGroupHeader && groupName && (_jsx("div", { className: "text-base-500 px-2 pt-2 pb-1 text-xs font-medium", children: groupName })), _jsx(CommandItem, { value: _getOptionId(option), onSelect: () => handleSelect(option), onMouseEnter: () => setHighlightedIndex(index), "data-autocomplete-item": true, className: cn('hover:bg-base-50 data-[selected=true]:bg-base-50 relative flex w-full cursor-pointer items-center gap-2 rounded-md py-2 pr-8 pl-2 text-sm outline-none select-none', isHighlighted && 'bg-base-50'), children: renderOption ? (renderOption(option, {
                        active: isHighlighted,
                        selected: !!isSelected,
                        disabled: false,
                    })) : (_jsxs(_Fragment, { children: [icon && (_jsx("span", { className: "text-base-500 flex shrink-0 items-center", children: icon })), _jsxs("div", { className: "flex flex-1 flex-col gap-0.5", children: [_jsx("span", { className: cn('truncate', isSelected && 'font-medium'), children: _getValue(option) }), subtitle && (_jsx("span", { className: "text-base-500 truncate text-xs", children: subtitle }))] }), isSelected && (_jsx("span", { className: "absolute right-2 flex items-center", children: _jsx(Check, { className: "text-brand-600 h-4 w-4" }) }))] })) })] }, _getOptionId(option)));
    };
    return (_jsxs("div", { className: cn('flex w-full flex-col gap-1', className), children: [title && _jsx(FieldTitle, { title: title, titlePosition: titlePosition }), _jsxs(Popover, { open: open, onOpenChange: setOpen, modal: modal, children: [_jsx(PopoverAnchor, { asChild: true, children: _jsx("div", { ref: containerRef, children: _jsx(Input, { type: "text", role: "combobox", "aria-expanded": open, disabled: disabled, readOnly: readOnly, placeholder: placeholder, value: inputValue, size: size, onChange: (e) => {
                                    const value = e.target.value;
                                    setQuery(value);
                                    onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(value);
                                    if (!open) {
                                        setOpen(true);
                                    }
                                }, onFocus: () => {
                                    if (!readOnly) {
                                        setOpen(true);
                                        setQuery('');
                                    }
                                }, onKeyDown: handleKeyDown, endIcon: _jsx(ChevronDown, { className: cn('text-base-500 pointer-events-none size-4 shrink-0 transition-transform', open && 'rotate-180') }) }) }) }), _jsx(PopoverContent, { className: "border-base-200 w-[var(--radix-popper-anchor-width)] rounded-lg border bg-white p-1 shadow-md", align: "start", onOpenAutoFocus: (e) => e.preventDefault(), onInteractOutside: (e) => {
                            var _a;
                            // Don't close if clicking the input container
                            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
                                e.preventDefault();
                            }
                        }, children: _jsx(Command, { shouldFilter: false, children: _jsx(CommandList, { className: "max-h-[300px]", ref: listRef, children: loading ? (_jsx("div", { className: "flex items-center justify-center py-6", children: _jsx("div", { className: "border-base-300 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" }) })) : (_jsxs(_Fragment, { children: [_jsx(CommandEmpty, { className: "text-base-500 py-6 text-center text-sm", children: emptyMessage }), _jsx(CommandGroup, { children: filtered.map((option, index) => {
                                                const groupName = getGroup === null || getGroup === void 0 ? void 0 : getGroup(option);
                                                const prevGroupName = index > 0 ? getGroup === null || getGroup === void 0 ? void 0 : getGroup(filtered[index - 1]) : undefined;
                                                const showGroupHeader = !!getGroup && groupName !== prevGroupName;
                                                return renderOptionItem(option, index, showGroupHeader, groupName);
                                            }) })] })) }) }) })] })] }));
}
